import ProjectDropdown from '../../ProjectDropdown';
import { useTranslation } from '../../../i18n';
import { SIDEBAR_MODE, DIALOG_VARIANT } from '../../../constants/types';
import useDialog from '../../../hooks/useDialog';
import './FormFields.scss';

/**
 * Title input field component
 */
export function TitleField({ value, onChange, placeholder, inputRef, disabled = false }) {
  const { t } = useTranslation();
  
  return (
    <div className="form-field">
      <label className="form-field__label">{t('event.title')}</label>
      <input
        ref={inputRef}
        type="text"
        className="form-field__input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        readOnly={disabled}
      />
    </div>
  );
}

/**
 * Description textarea field component
 */
export function DescriptionField({ value, onChange, disabled = false }) {
  const { t } = useTranslation();
  
  return (
    <div className="form-field">
      <label className="form-field__label">{t('event.description')}</label>
      <textarea
        className="form-field__textarea"
        placeholder={t('event.descriptionPlaceholder')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        disabled={disabled}
        readOnly={disabled}
      />
    </div>
  );
}

/**
 * Project dropdown field component
 */
export function ProjectField({ value, onChange, projects, disabled = false }) {
  const { t } = useTranslation();
  
  if (projects.length === 0) return null;
  
  return (
    <div className="form-field">
      <label className="form-field__label">{t('event.project')}</label>
      <ProjectDropdown
        projects={projects}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Form action buttons (Delete, Cancel, Save/Create)
 */
export function FormActions({ 
  mode, 
  entityType,
  entityTitle,
  onDelete, 
  onClose, 
  onSubmit,
  canSubmit,
  disabled = false
}) {
  const { t } = useTranslation();
  const { confirm: confirmDialog } = useDialog();
  const isEditing = mode === SIDEBAR_MODE.EDIT;

  const handleDelete = async () => {
    const titleKey = entityType === 'event' ? 'dialog.deleteEvent' : 'dialog.deleteMilestone';
    const messageKey = entityType === 'event' ? 'dialog.deleteEventConfirm' : 'dialog.deleteMilestoneConfirm';
    
    const confirmed = await confirmDialog({
      title: t(titleKey),
      message: t(messageKey, { title: entityTitle }),
      confirmLabel: t('common.delete'),
      variant: DIALOG_VARIANT.DANGER
    });
    
    if (confirmed) {
      onDelete();
    }
  };

  return (
    <div className="form-actions">
      {isEditing && !disabled && (
        <button 
          type="button" 
          className="form-actions__btn form-actions__btn--delete" 
          onClick={handleDelete}
        >
          {t('common.delete')}
        </button>
      )}
      <div className="form-actions__right">
        <button 
          type="button" 
          className="form-actions__btn form-actions__btn--cancel" 
          onClick={onClose}
        >
          {t('common.cancel')}
        </button>
        {!disabled && (
          <button 
            type="submit" 
            className="form-actions__btn form-actions__btn--confirm" 
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {isEditing ? t('common.save') : t('common.create')}
          </button>
        )}
      </div>
    </div>
  );
}

const FormFields = {
  TitleField,
  DescriptionField,
  ProjectField,
  FormActions
};

export default FormFields;
